"use client";
import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { cn } from "@/lib/utils";
import { CONVERSION_PAGE_PATH } from "@/lib/conversion-page";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { ExtractPropertyType } from "types";
import { type NavMenuQueryResult } from "@shared/sanity/types";
import { useAppSelector } from "@/store/hooks";
import { iconsMap } from "../icons";

export type MenuItemType = ExtractPropertyType<NavMenuQueryResult, "menuItems">[number];

export const getHref = (linkData: ExtractPropertyType<MenuItemType, "link">) => {
  if (!linkData) return "coming-soon";

  if ("external" in linkData) {
    return linkData.external || "not-found";
  }

  if ("internal" in linkData && linkData.internal?.slug?.current) {
    return `/${linkData.internal.slug.current}`;
  }

  return "coming-soon";
};

export const isExternalLink = (linkData: ExtractPropertyType<MenuItemType, "link">) => {
  return linkData?.type === "external";
};

function MenuItem({ item }: { item: MenuItemType }) {
  const { theme } = useAppSelector((state) => state.header);

  return item.children ? (
    <NavigationMenuItem>
      <NavigationMenuTrigger
        data-theme={theme}
        className="bg-transparent text-transparent data-[active]:text-primary-text-700 data-[state=open]:text-primary-text-800 relative before:absolute before:content-[''] before:w-0 before:h-0 data-[state=open]:before:w-4 data-[state=open]:before:h-4 before:bg-transparent data-[state=open]:before:bg-primary-50 before:-bottom-4 before:rotate-45 before:rounded-sm transition-colors before:duration-150 data-[theme=dark]:dark data-[theme]:text-primary-text-600"
        aria-label={item.ariaLabel || item.text}
      >
        {item.text}
      </NavigationMenuTrigger>
      <NavigationMenuContent className="p-10 bg-primary-50">
        <p className="uppercase font-semibold text-primary-200 text-sm mb-4">{item.description}</p>
        <ul
          className={`grid w-[400px] md:w-[500px] gap-y-4 md:grid-cols-[auto_auto] lg:w-[630px] rounded-lg`}
        >
          {item.children.map((child) => {
            const href = getHref(child.link);
            const isExternal = isExternalLink(child.link);

            return (
              <ListItem
                key={child._key}
                title={child.text}
                href={href}
                icon={child.icon ? iconsMap[child.icon] : undefined}
                isExternal={isExternal}
                ariaLabel={child.ariaLabel}
              >
                {child.description}
              </ListItem>
            );
          })}
        </ul>
      </NavigationMenuContent>
    </NavigationMenuItem>
  ) : (
    <NavigationMenuItem>
      {isExternalLink(item.link) ? (
        // External link - use regular anchor tag
        <a
          href={getHref(item.link)}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            navigationMenuTriggerStyle(),
            "bg-transparent data-[theme]:text-primary-text-600 data-[active]:text-primary-text-700 data-[state=open]:text-primary-text-800 data-[theme=dark]:dark text-transparent"
          )}
          data-theme={theme}
          aria-label={item.ariaLabel || item.text}
        >
          {item.text}
        </a>
      ) : (
        // Internal link - use Next.js Link

        <NavigationMenuLink asChild>
          <Link
            href={getHref(item.link)}
            data-theme={theme}
            className={cn(
              navigationMenuTriggerStyle(),
              "bg-transparent data-[theme]:text-primary-text-600 data-[active]:text-primary-text-700 data-[state=open]:text-primary-text-800 data-[theme=dark]:dark text-transparent"
            )}
            aria-label={item.ariaLabel || item.text}
          >
            {item.text}
          </Link>
        </NavigationMenuLink>
      )}
    </NavigationMenuItem>
  );
}

export interface NavMenuProps {
  menuItems: MenuItemType[];
}

export function NavMenu({ menuItems }: NavMenuProps) {
  const isConversionPage = usePathname() === CONVERSION_PAGE_PATH;

  if (isConversionPage) {
    return null;
  }

  return (
    <NavigationMenu className="hidden xl:block">
      <NavigationMenuList>
        {menuItems.map((item) => (
          <MenuItem item={item} key={item.text} />
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
}

interface ListItemProps extends React.ComponentPropsWithoutRef<"a"> {
  title: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  isExternal?: boolean;
  ariaLabel?: string;
}

const ListItem = React.forwardRef<React.ElementRef<"a">, ListItemProps>(
  ({ className, title, icon, children, isExternal = false, ariaLabel, ...props }, ref) => {
    const linkProps = isExternal
      ? {
          target: "_blank",
          rel: "noopener noreferrer",
          ...props,
        }
      : props;

    return (
      <li>
        <NavigationMenuLink asChild>
          <a
            ref={ref}
            className={cn(
              "select-none flex flex-row gap-2 text-2xl space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-primary-100/20 hover:text-primary-600 text-primary-500 focus:bg-primary-100/20 focus:text-primary-600",
              className
            )}
            aria-label={ariaLabel || title}
            {...linkProps}
          >
            <div className="my-1">{icon}</div>
            <div>
              <h5 className="capitalize text-base font-medium leading-none text-primary-900">
                {title}
              </h5>
              <p className="line-clamp-2 text-sm leading-snug text-primary-300">{children}</p>
            </div>
          </a>
        </NavigationMenuLink>
      </li>
    );
  }
);
ListItem.displayName = "ListItem";
